import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {concatMap, shareReplay} from 'rxjs/operators';
import {ApiLocationService} from '@services/api-location/api-location.service';

import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {LocationDetails} from '@models/locationDetails/location-details';
import {NotificationService} from '@services/notification/notification.service';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';

@Injectable({
  providedIn: 'root'
})
export class GeocodingService {
  private readonly BASE_URL: string;
  private MACHINE_LOCATION_URL_SUFFIX = '/api/web/admin/machine/location/:address';
  private MACHINE_BROWSER_KEY_URL_SUFFIX = '/api/web/admin/machine/locationApyKey';
  private GOOGLE_MAPS_API_URL = 'https://maps.googleapis.com/maps/api/js?libraries=places&key=:key';
  // TODO wait for lazy loading module feature
  // private googleApiKeySubject = new BehaviorSubject<string | null>(null);
  // readonly googleApiKey$ = this.googleApiKeySubject.asObservable().pipe(
  //   filter(key => !!key)
  // );
  googleMapsLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private static googleMapsApiLoaded = false;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private notification: NotificationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  // TODO: wait for lazy loading modules feature
  // get GOOGLE_API_KEY () {
  //   return this.googleApiKeySubject.getValue();
  // }

  public getLocation(addressOrCoordinates: string): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_LOCATION_URL_SUFFIX;
    url = url.replace(':address', addressOrCoordinates);
    return this.http.get<LocationDetails>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public loadMapsApiObservable(): Observable<any> {
    return this.getGeocodingKey().pipe(concatMap((keys: string[]) => this.loadApiWithKeyObservable(keys)));
  }

  private loadApiWithKeyObservable(keys: string[]): Observable<any> {
    let key = '';

    if (keys && keys.length > 0) {
      key = keys[0];
    }

    const url = this.GOOGLE_MAPS_API_URL.replace(':key', key);
    return this.http.jsonp(url, 'callback');
  }

  public loadMapsApi(): void {
    this.getGeocodingKey().subscribe((key: string[]) => {
      if (key && key.length > 0) {
        this.loadApiWithKey(key[0]);
      }
    }, (error) => {
      console.error(JSON.stringify(error));
      this.loadApiWithKey('');
    });
  }

  private loadApiWithKey(key: string | null): void {
    if (GeocodingService.googleMapsApiLoaded) {
      this.googleMapsLoaded.next(true);
    } else {
      const url = this.GOOGLE_MAPS_API_URL.replace(':key', key ? key : '');
      this.http.jsonp(url, 'callback').subscribe(() => {
        GeocodingService.googleMapsApiLoaded = true;
        console.log('Google Geocoding Api googleMapsApiLoaded successfully');
        this.googleMapsLoaded.next(true);
      }, (error) => {
        console.error('Google Geocoding Api loading error. ' + JSON.stringify(error));
      });
    }
  }

  private getGeocodingKey(): Observable<any> {
    const url: string = this.BASE_URL + this.MACHINE_BROWSER_KEY_URL_SUFFIX;
    return this.http.get<string[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  // TODO: wait for lazy loading modules feature
  // public saveGeocodingKey(): void {
  //   const url: string = this.BASE_URL + this.MACHINE_BROWSER_KEY_URL_SUFFIX;
  //   const UILanguage = this.storageService.readValueFromLocalStorage('UILanguage');
  //   if (UILanguage) {
  //     this.http.get<string[]>(url, HTTPS_REQUEST_OPTIONS).subscribe({
  //       next: (apiKeys) => this.googleApiKeySubject.next(apiKeys[0]),
  //       error: () => this.googleApiKeySubject.next('')
  //     });
  //   } else {
  //     this.googleApiKeySubject.next('unauthorised');
  //   }
  // }

  public getCurrentPosition(): Observable<GeolocationPosition> {
    return new Observable(
      (observer) => {
        navigator.geolocation.watchPosition((pos: GeolocationPosition) => {
          observer.next(pos);
        });
      });
  }

  public getVisibleMachines(bounds: google.maps.LatLngBounds, possibleMachines: DashboardMachine[], markers: google.maps.Marker[]): DashboardMachine[] {
    const array: DashboardMachine[] = [];
    markers.forEach((marker) => {
      if (bounds.contains(marker.getPosition() as google.maps.LatLng)) {
        const machine = possibleMachines.find(({id}) => id === marker.get('options').machineId);
        if (machine) {
          array.push(machine);
        }
      }
    });
    return array;
  }

  public parsePlaceResultToLocationDetails(placeResult?: google.maps.places.PlaceResult): LocationDetails {
    let locationDetails: LocationDetails = new LocationDetails();
    if (!placeResult) {
      return locationDetails;
    }
    locationDetails = this.determineCoordinates(locationDetails, placeResult);
    locationDetails = this.determineAddress(locationDetails, placeResult);

    return locationDetails;
  }

  private determineCoordinates(locationToUpdate: LocationDetails, placeResult: google.maps.places.PlaceResult): LocationDetails {
    if (!placeResult?.geometry?.location) {
      return locationToUpdate;
    }

    locationToUpdate.latitude = placeResult.geometry.location.lat();
    locationToUpdate.longitude = placeResult.geometry.location.lng();
    return locationToUpdate;
  }


  private determineAddress(locationToUpdate: LocationDetails, geocodingResult: google.maps.places.PlaceResult): LocationDetails {
    const addressComponents: google.maps.GeocoderAddressComponent[] | undefined = geocodingResult.address_components;
    if (!addressComponents) {
      return locationToUpdate;
    }
    for (const addressComponent of addressComponents) {
      const type = addressComponent.types[0];
      switch (type) {
        case 'street_number': {
          locationToUpdate.number = addressComponent.long_name;
          break;
        }
        case 'street_address':
        case 'route': {
          locationToUpdate.street = addressComponent.long_name;
          break;
        }
        case 'country': {
          locationToUpdate.countryCode = addressComponent.short_name;
          locationToUpdate.country = addressComponent.long_name;
          break;
        }
        case 'locality': {
          locationToUpdate.regionCity = addressComponent.long_name;
          break;
        }
        case 'postal_code': {
          locationToUpdate.postalCode = addressComponent.long_name;
          break;
        }
        default: {
          console.log('Unsupported field: ' + type + ' -> ' + addressComponent.long_name);
          break;
        }
      }
    }


    return locationToUpdate;
  }

  public getGeocodingSearchValue(isAddress: boolean, address: string, latitude: string, longitude: string): string {
    let searchValue: string = '';
    if (isAddress) {
      if (this.isValueInvalid(address, 'address')) {
        return searchValue;
      }
      searchValue = address;
    } else {
      if (this.isValueInvalid(latitude, 'latitude')) {
        return searchValue;
      }
      if (this.isValueInvalid(longitude, 'longitude')) {
        return searchValue;
      }
      searchValue = latitude + ',' + longitude;
    }
    return searchValue;
  }

  private isValueInvalid(value: string, type: string): boolean {
    if (!value || value.length === 0 || value.trim().length === 0) {
      this.showInvalidValueWarning(value, type);
      return true;
    }

    if (type === 'latitude' || type === 'longitude') {
      if (isNaN(Number(value))) {
        this.showInvalidValueWarning(value, type);
        return true;
      }
    }

    return false;
  }

  private showInvalidValueWarning(value: string, type: string): void {
    this.notification.warning('The value "' + value + '" of ' + type + ' is invalid!', 'Machine location service');
  }

  public updateMachinesPosition(array: DashboardMachine[]): DashboardMachine[] {
    const newArray: DashboardMachine[][] = [];
    return array.reduce((acc, next) => {

      const group = acc.find(group => {
        return group.find(item => {
          return next.datasetLocation === item.datasetLocation &&
                 next.location === item.location &&
                 next.locationDetails?.latitude === item.locationDetails?.latitude &&
                 next.locationDetails?.longitude === item.locationDetails?.longitude;
        });
      });

      !!group ? group.push({...next}) : acc.push([{...next}]);

      return acc;
    }, newArray)
    .map(group => this.createPositionsGrid(group))
    .reduce((acc, next) => (acc.concat(next)), []);
  }

  public createPositionsGrid (array: DashboardMachine[]) {
    if (array.length > 9) {
      return array;
    }
    return array.map((item, position) => this.shiftPosition(item, position));
  }

  private shiftPosition(item: DashboardMachine, position: number): DashboardMachine {
    const shiftedPositionItem: DashboardMachine = JSON.parse(JSON.stringify(item));
    const shift = 0.00005;

    if (shiftedPositionItem.locationDetails && shiftedPositionItem.locationDetails.longitude && shiftedPositionItem.locationDetails.latitude) {
      switch (position) {
        case 1: {
          shiftedPositionItem.locationDetails.longitude += shift;
          break;
        }
        case 2: {
          shiftedPositionItem.locationDetails.longitude -= shift;
          break;
        }
        case 3: {
          shiftedPositionItem.locationDetails.latitude -= shift;
          break;
        }
        case 4: {
          shiftedPositionItem.locationDetails.latitude += shift;
          break;
        }
        case 5: {
          shiftedPositionItem.locationDetails.longitude += shift;
          shiftedPositionItem.locationDetails.latitude += shift;
          break;
        }
        case 6: {
          shiftedPositionItem.locationDetails.longitude -= shift;
          shiftedPositionItem.locationDetails.latitude += shift;
          break;
        }
        case 7: {
          shiftedPositionItem.locationDetails.longitude += shift;
          shiftedPositionItem.locationDetails.latitude -= shift;
          break;
        }
        case 8: {
          shiftedPositionItem.locationDetails.longitude -= shift;
          shiftedPositionItem.locationDetails.latitude -= shift;
          break;
        }
      }
    }
    return shiftedPositionItem;
  }
}
